import type {DetalhesComprasGov} from '../../../types/priceResearch'
import {formatCnpj} from '../../RadarFornecedor/format'

// Modelo de exibição do modal "Detalhes do preço — Compras.gov.br": transforma o registro
// guardado na coleta (`detalhes_comprasgov`) em seções de linhas rótulo/valor. Linha sem
// dado some; seção sem linha some. Nomes e unidades saem EXATAMENTE como o JSON os entrega.

export type LinhaDetalhe = {
    rotulo: string
    valor: string
    destaque?: string // prefixo do valor desenhado em negrito (o preço)
    resto?: string // o que vem depois do destaque; destaque + resto === valor
    mono?: boolean // número/CNPJ em fonte monoespaçada
}
export type SecaoDetalhe = {titulo: string; linhas: LinhaDetalhe[]}

const HOST_COMPRASGOV = 'dadosabertos.compras.gov.br'

const MODALIDADES: Record<number, string> = {
    1: 'Convite',
    2: 'Tomada de Preços',
    3: 'Concorrência',
    4: 'Concorrência Internacional',
    5: 'Pregão',
    6: 'Dispensa de licitação',
    7: 'Inexigibilidade',
    20: 'Concurso',
}
const FORMAS: Record<string, string> = {SISPP: 'compra comum (SISPP)', SISRP: 'registro de preços (SISRP)'}
const ESFERAS: Record<string, string> = {F: 'Federal', E: 'Estadual', M: 'Municipal'}

const fmtNumero = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 3})
const fmtBrl = new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'})

// O Intl devolve um espaço não separável depois de "R$"; texto copiado da tela fica com espaço comum.
const brl = (n: number) => fmtBrl.format(n).replace(/ /g, ' ')

/** Texto presente (não nulo, não vazio, não só espaços), exatamente como veio. */
function txt(v: unknown): string | null {
    if (v === null || v === undefined) return null
    const s = String(v)
    return s.trim() === '' ? null : s
}

function num(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

function unidade(d: DetalhesComprasGov): string | null {
    return txt(d.nomeUnidadeFornecimento) ?? txt(d.nomeUnidadeMedida) ?? txt(d.siglaUnidadeFornecimento)
}

function precoUnitario(d: DetalhesComprasGov): LinhaDetalhe | null {
    const p = num(d.precoUnitario)
    if (p === null) return null
    const destaque = brl(p)
    let resto = ''
    const un = unidade(d)
    if (un) {
        resto = ` por ${un}`
        const cap = num(d.capacidadeUnidadeFornecimento)
        const sigla = txt(d.siglaUnidadeMedida)
        if (cap !== null && cap > 0 && sigla) resto += ` (${fmtNumero.format(cap)} ${sigla.toLowerCase()})`
    }
    return {rotulo: 'Preço unitário', valor: destaque + resto, destaque, resto}
}

function quantidade(d: DetalhesComprasGov): LinhaDetalhe | null {
    const q = num(d.quantidade)
    if (q === null) return null
    const un = unidade(d)
    return {rotulo: 'Quantidade', valor: un ? `${fmtNumero.format(q)} ${un}` : fmtNumero.format(q)}
}

function catalogo(d: DetalhesComprasGov): string | null {
    const cod = txt(d.codigoItemCatalogo)
    if (!cod) return null
    if (d.tipo === 'servico') return `CATSER ${cod}`
    if (d.tipo !== 'material') return null
    const pdm = txt(d.codigoPdm)
    const nome = txt(d.nomePdm)
    return `CATMAT ${cod}${pdm ? ` · PDM ${pdm}` : ''}${nome ? ` — ${nome}` : ''}`
}

function modalidade(d: DetalhesComprasGov): string | null {
    const cod = txt(d.modalidade)
    if (!cod) return null
    const rotulo = MODALIDADES[Number(cod)] ?? `Modalidade ${cod}`
    const forma = txt(d.forma)
    return forma && FORMAS[forma] ? `${rotulo} · ${FORMAS[forma]}` : rotulo
}

// Número da compra = dígitos 9–13 / 14–17 do idCompra (17 dígitos, sempre string).
function numeroCompra(d: DetalhesComprasGov): string | null {
    const id = txt(d.idCompra)
    if (!id || !/^\d{17}$/.test(id)) return null
    const item = num(d.numeroItemCompra)
    return `${id.slice(8, 13)}/${id.slice(13, 17)}${item !== null ? ` · item ${item}` : ''}`
}

function dataBr(v: unknown): string | null {
    const s = txt(v)
    if (!s) return null
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s
}

function uasg(d: DetalhesComprasGov): string | null {
    const cod = txt(d.codigoUasg)
    const nome = txt(d.nomeUasg)
    return cod && nome ? `${cod} — ${nome}` : cod ?? nome
}

function local(d: DetalhesComprasGov): string | null {
    const municipio = txt(d.municipio)
    const estado = txt(d.estado)
    const esfera = txt(d.esfera)
    const cidade = [municipio, estado].filter(Boolean).join('/')
    const partes = [cidade, esfera ? ESFERAS[esfera] ?? esfera : null].filter(Boolean)
    return partes.length ? partes.join(' · ') : null
}

function cnpj(d: DetalhesComprasGov): string | null {
    const ni = txt(d.niFornecedor)
    if (!ni) return null
    const digitos = ni.replace(/\D/g, '')
    return digitos.length === 14 ? formatCnpj(digitos) : ni
}

function linha(rotulo: string, valor: string | null, extra: Partial<LinhaDetalhe> = {}): LinhaDetalhe | null {
    return valor === null ? null : {rotulo, valor, ...extra}
}

/** Seções do modal; linhas vazias e seções sem linha não aparecem. */
export function montarSecoes(d: DetalhesComprasGov): SecaoDetalhe[] {
    const secoes: {titulo: string; linhas: (LinhaDetalhe | null)[]}[] = [
        {
            titulo: 'Item',
            linhas: [
                linha('Descrição', txt(d.descricaoDetalhadaItem) ?? txt(d.descricaoItem)),
                linha('Marca', txt(d.marca)),
                precoUnitario(d),
                quantidade(d),
                linha('Catálogo', catalogo(d)),
            ],
        },
        {
            titulo: 'Compra',
            linhas: [
                linha('Modalidade', modalidade(d)),
                linha('Número', numeroCompra(d), {mono: true}),
                linha('Data do resultado', dataBr(d.dataResultado) ?? dataBr(d.dataCompra)),
                linha('Objeto', txt(d.objetoCompra)),
            ],
        },
        {
            titulo: 'Órgão comprador',
            linhas: [linha('Órgão', txt(d.nomeOrgao)), linha('UASG', uasg(d)), linha('Local', local(d))],
        },
        {
            titulo: 'Fornecedor',
            linhas: [linha('Razão social', txt(d.nomeFornecedor)), linha('CNPJ', cnpj(d), {mono: true})],
        },
    ]
    return secoes
        .map((s) => ({titulo: s.titulo, linhas: s.linhas.filter((l): l is LinhaDetalhe => l !== null)}))
        .filter((s) => s.linhas.length > 0)
}

/** True quando a linha de revisão abre o modal em vez do link "abrir" (CGMOD-06/10/11). */
export function temDetalhesComprasGov<
    T extends {origem?: string | null; link?: string | null; detalhes?: DetalhesComprasGov | null},
>(l: T): l is T & {link: string; detalhes: DetalhesComprasGov} {
    if (l.origem !== 'COMPRASGOV' || !l.link || !l.detalhes || Object.keys(l.detalhes).length === 0) return false
    try {
        const url = new URL(l.link)
        return url.protocol === 'https:' && url.hostname === HOST_COMPRASGOV
    } catch {
        return false
    }
}
